'use client'

import { useState, useRef, useEffect } from 'react'
import { formatPrice, formatChange } from '@/lib/formatters'

interface Prediction {
  label: string
  date: string
  days: number
  low: number
  high: number
  change: number
}

interface Props {
  historicalPrices: [number, number][] // [timestamp, price]
  predictions: Prediction[]
  currentPrice: number
}

function Arrow({ positive }: { positive: boolean }) {
  return positive ? (
    <svg width="12" height="12" viewBox="0 0 8 8" fill="currentColor" className="inline">
      <path d="M4 1L7 5H1L4 1Z" />
    </svg>
  ) : (
    <svg width="12" height="12" viewBox="0 0 8 8" fill="currentColor" className="inline">
      <path d="M4 7L1 3H7L4 7Z" />
    </svg>
  )
}

function fmtCompact(n: number): string {
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K'
  if (n >= 1) return '$' + n.toFixed(0)
  return '$' + n.toPrecision(3)
}

function fmtPct(n: number): string {
  return (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
}

function Chart({
  historicalPrices,
  predictions,
  currentPrice,
  activeRange,
}: Props & { activeRange: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dims, setDims] = useState({ w: 800, h: 300 })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect
      setDims({ w: width, h: Math.min(300, width * 0.4) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const { w, h } = dims
  const padR = 30
  const padT = 20
  const padB = 30

  const forecast = predictions.find((f) => f.label === activeRange) || predictions[0]
  const predMid = (forecast.low + forecast.high) / 2

  // Sample historical to ~60 points for performance
  const step = Math.max(1, Math.floor(historicalPrices.length / 60))
  const sampled = historicalPrices.filter((_, i) => i % step === 0 || i === historicalPrices.length - 1)

  const histPrices = sampled.map((p) => p[1])
  const minPrice = Math.min(...histPrices, forecast.low) * 0.98
  const maxPrice = Math.max(...histPrices, forecast.high) * 1.02
  const histCount = sampled.length
  const totalDays = histCount + forecast.days

  const x = (i: number) => (i / totalDays) * (w - padR)
  const y = (price: number) =>
    padT + ((maxPrice - price) / (maxPrice - minPrice)) * (h - padT - padB)

  // Historical path
  const histPath = sampled
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p[1]).toFixed(1)}`)
    .join(' ')

  // Area fill
  const histArea =
    histPath +
    ` L${x(histCount - 1).toFixed(1)},${h - padB} L${x(0).toFixed(1)},${h - padB} Z`

  const lastX = x(histCount - 1)
  const lastY = y(currentPrice)
  const predX = x(histCount + forecast.days)
  const predY = y(predMid)
  const predHighY = y(forecast.high)
  const predLowY = y(forecast.low)

  const isPositive = forecast.change >= 0
  const accentColor = isPositive ? '#22c55e' : '#ef4444'
  const accentFill = isPositive ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)'

  // Grid
  const gridCount = 5
  const gridLines = Array.from({ length: gridCount }, (_, i) => {
    const price = minPrice + ((maxPrice - minPrice) * (gridCount - 1 - i)) / (gridCount - 1)
    return { y: y(price), label: fmtCompact(price) }
  })

  return (
    <div ref={containerRef} className="w-full" style={{ minHeight: h }}>
      <svg width={w} height={h} className="w-full">
        {/* Grid */}
        {gridLines.map((g, i) => (
          <g key={i}>
            <line x1={0} y1={g.y} x2={w - padR} y2={g.y} stroke="#1c1c20" strokeWidth="0.5" />
            <text x={w - padR + 4} y={g.y + 3} fill="#71717a" fontSize="10" textAnchor="start">
              {g.label}
            </text>
          </g>
        ))}

        {/* Forecast range shading */}
        <path
          d={`M${lastX},${predHighY} L${predX},${predHighY} L${predX},${predLowY} L${lastX},${predLowY} Z`}
          fill={accentFill}
        />

        {/* Forecast zone tint */}
        <rect
          x={lastX}
          y={padT}
          width={w - padR - lastX}
          height={h - padT - padB}
          fill="rgba(255,255,255,0.015)"
        />

        {/* Historical area */}
        <defs>
          <linearGradient id="predHistGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={histArea} fill="url(#predHistGrad)" />

        {/* Historical line */}
        <path d={histPath} fill="none" stroke="#3b82f6" strokeWidth="1.5" />

        {/* Today divider */}
        <line
          x1={lastX}
          y1={padT}
          x2={lastX}
          y2={h - padB}
          stroke="#3f3f46"
          strokeWidth="1"
          strokeDasharray="4,4"
        />

        {/* Prediction — midpoint dotted line */}
        <line
          x1={lastX}
          y1={lastY}
          x2={predX}
          y2={predY}
          stroke={accentColor}
          strokeWidth="1.5"
          strokeDasharray="6,4"
        />

        {/* Prediction — high range line */}
        <line
          x1={lastX}
          y1={lastY}
          x2={predX}
          y2={predHighY}
          stroke={accentColor}
          strokeWidth="0.5"
          strokeDasharray="3,3"
          opacity="0.4"
        />

        {/* Prediction — low range line */}
        <line
          x1={lastX}
          y1={lastY}
          x2={predX}
          y2={predLowY}
          stroke={accentColor}
          strokeWidth="0.5"
          strokeDasharray="3,3"
          opacity="0.4"
        />

        {/* Endpoint dot */}
        <circle cx={predX} cy={predY} r="3" fill={accentColor} />

        {/* Labels */}
        <text x={lastX - 8} y={h - 10} fill="#71717a" fontSize="10" textAnchor="end">
          Today
        </text>
        <text x={predX} y={h - 10} fill="#71717a" fontSize="10" textAnchor="middle">
          {forecast.label}
        </text>
      </svg>
    </div>
  )
}

export function PredictionForecast({ historicalPrices, predictions, currentPrice }: Props) {
  const [activeRange, setActiveRange] = useState(predictions[0]?.label || '1 week')

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Chart — left */}
      <div className="border border-zinc-800 p-5 flex-1 min-w-0">
        <div className="flex gap-2 mb-4">
          {predictions.map((f) => (
            <button
              key={f.label}
              onClick={() => setActiveRange(f.label)}
              className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                activeRange === f.label
                  ? 'border-zinc-400 text-white'
                  : 'border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <Chart
          historicalPrices={historicalPrices}
          predictions={predictions}
          currentPrice={currentPrice}
          activeRange={activeRange}
        />
      </div>

      {/* Cards — right */}
      <div className="flex flex-col gap-4 lg:w-72 shrink-0">

        {predictions.map((pred) => (
          <button
            key={pred.label}
            onClick={() => setActiveRange(pred.label)}
            className={`border p-4 text-left transition-colors lg:flex-1 ${

              activeRange === pred.label
                ? 'border-zinc-400'
                : 'border-zinc-800 hover:border-zinc-700'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-white text-sm font-medium">
                {pred.label}
                <span className="text-zinc-400 text-xs font-normal ml-2">By {pred.date}</span>
              </p>
              <span
                className={`text-sm font-bold tabular-nums inline-flex items-center gap-1 ${
                  pred.change >= 0 ? 'text-green-500' : 'text-red-500'
                }`}
              >
                <Arrow positive={pred.change >= 0} />
                {fmtPct(pred.change)}
              </span>
            </div>
            <div className="flex justify-between">
              <div>
                <p className="text-zinc-400 text-xs mb-0.5">Low</p>
                <p className="text-zinc-300 text-sm tabular-nums">{formatPrice(pred.low)}</p>
              </div>
              <div className="text-right">
                <p className="text-zinc-400 text-xs mb-0.5">High</p>
                <p className="text-zinc-300 text-sm tabular-nums">{formatPrice(pred.high)}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}